/**
 * Consonant Review Service
 *
 * Manages the 6-prompt consonant practice session flow:
 *   VP1: Sound introduction + listen
 *   VP2: Illustration + image live videos + drawing
 *   VP3: Repetition practice (3x call-and-response)
 *   VP4a/4b: Lowercase tracing (passive video / active trace)
 *   VP5a/5b: Uppercase tracing (passive video / active trace)
 *   VP6: Mastery selection (UI-only, no audio)
 *
 * All scripts are fixed (no LLM dependency). Phoneme .m4a files play
 * at full natural duration — matching the voice agent's proven approach.
 *
 * Audio gain uses anti-clip ceiling (0.95 / peak) in the audio engine.
 * No standalone punctuation TTS — ElevenLabs produces garbage for bare "?" or "!".
 *
 * @see useConsonantReviewFlow.ts for state machine details
 * @see audio-engine.ts playPhoneme() — the same function the voice agent uses
 */

import type { AudioSegment } from '@/lib/voice-agent/audio-engine';

// =============================================================================
// Constants
// =============================================================================

/** Prescribed card order for guided sessions */
export const CARD_ORDER = [
  'b', 'c', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm',
  'n', 'p', 'r', 's', 't', 'v', 'w', 'x', 'y', 'z',
] as const;

export type ConsonantLetter = typeof CARD_ORDER[number];

// =============================================================================
// Illustration Map (Static — 20 consonants)
// =============================================================================

/**
 * Maps each consonant to its illustration word.
 * These are fixed associations used across all sessions.
 * The illustrated card image depicts this specific subject.
 *
 * When updating: also update qr_codes.action_payload in DB
 * and ILLUSTRATION_DESCRIPTIONS below.
 */
export const ILLUSTRATION_MAP: Record<string, string> = {
  b: 'butterfly',
  c: 'cookie',
  d: 'dog',
  f: 'flag',
  g: 'goat',
  h: 'house',
  j: 'jelly',
  k: 'kite',
  l: 'lion',
  m: 'mountain',
  n: 'nest',
  p: 'penguin',
  r: 'rainbow',
  s: 'snake',
  t: 'table',
  v: 'violin',
  w: 'wave',
  x: 'box',
  y: 'yarn',
  z: 'zebra',
};

export const ILLUSTRATION_DESCRIPTIONS: Record<string, string> = {
  b: 'butterfly',
  c: 'chocolate chip cookie',
  d: 'dog',
  f: 'flag on a flagpole',
  g: 'goat',
  h: 'house with chimney',
  j: 'jelly',
  k: 'kite',
  l: 'lion',
  m: 'mountain',
  n: 'nest',
  p: 'penguin',
  r: 'rainbow',
  s: 'snake',
  t: 'table',
  v: 'violin',
  w: 'wave in the ocean, left side curling into a crest',
  x: 'box',
  y: 'yarn',
  z: 'zebra',
};

/**
 * Letter name map — how the TTS should pronounce each letter name.
 * Most letters use their uppercase form directly; override only
 * those whose name differs from naively reading the character.
 */
const LETTER_NAMES: Record<string, string> = {
  c: 'see',
  g: 'jee',
  h: 'aitch',
  j: 'jay',
  k: 'kay',
  r: 'are',
  w: 'double you',
  x: 'ex',
  y: 'why',
};

// =============================================================================
// Helpers
// =============================================================================

/** Get the spoken letter name for TTS (e.g., 'b' -> 'B', 'w' -> 'double you') */
export function getLetterName(phoneme: string): string {
  return LETTER_NAMES[phoneme.toLowerCase()] || phoneme.toUpperCase();
}

/** Get the illustration word for a consonant phoneme */
export function getIllustrationWord(phoneme: string): string {
  return ILLUSTRATION_MAP[phoneme.toLowerCase()] || phoneme;
}

/** Legacy no-op kept for API compatibility */
export function clearPhraseCache(): void {}

// =============================================================================
// Prompt Audio Builders
// =============================================================================

/**
 * VP1 — Sound Introduction + Listen
 *
 * "Letter [letter name] makes the sound [letter sound]!
 *  Can you say [letter sound]?"
 *
 * After audio finishes, parent waits for child to respond then taps Continue.
 * If recording is enabled, the listen window captures child audio.
 */
export function getPrompt1Audio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Letter ${name} makes the sound` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 200 },
    { type: 'tts', text: `Can you say` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
  ];
}

/**
 * VP2 — Illustration + Drawing
 *
 * "Great job! Let's work hard to remember [letter sound]…
 *  this will help: [letter sound] looks like a [illustration]!
 *  {plays lowercase image live video, then uppercase image live video}
 *  It's your turn to draw! Can you draw a [illustration] like mine?
 *  You can use colored markers to draw your very own [illustration].
 *  Take your time, and let me know when you're ready to continue!"
 *
 * The video playback is handled by the state machine, not the audio engine.
 * This builder returns audio for the pre-video and post-video TTS segments.
 */
export function getPrompt2PreVideoAudio(phoneme: string, illustrationWord: string): AudioSegment[] {
  return [
    { type: 'tts', text: `Great job! Let's work hard to remember` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 200 },
    { type: 'tts', text: `this will help:` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'tts', text: `looks like a ${illustrationWord}!` },
  ];
}

export function getPrompt2PostVideoAudio(illustrationWord: string): AudioSegment[] {
  return [
    { type: 'tts', text: `It's your turn to draw! Can you draw a ${illustrationWord} like mine? You can use colored markers to draw your very own ${illustrationWord}. Take your time, and let me know when you're ready to continue!` },
  ];
}

/**
 * VP3 — Repetition Practice (3x)
 *
 * "Let's practice. Letter [name] makes the sound [sound], [sound] like [image].
 *  Can you repeat after me? Listen carefully:
 *  Letter [name] makes the sound [sound], [sound] like [image]. (listen)
 *  Great job! One more time. Listen carefully:
 *  Letter [name] makes the sound [sound], [sound] like [image]. (listen)
 *  Excellent! One more time. Listen carefully:
 *  Letter [name] makes the sound [sound], [sound] like [image]."
 */
export function getPrompt3Audio(phoneme: string, illustrationWord: string): AudioSegment[] {
  const name = getLetterName(phoneme);

  const corePhrase = (prefix: string): AudioSegment[] => [
    { type: 'tts', text: `${prefix} Letter ${name} makes the sound` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'tts', text: `like ${illustrationWord}.` },
  ];

  return [
    { type: 'tts', text: `Let's practice. Letter ${name} makes the sound` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'tts', text: `like ${illustrationWord}. Can you repeat after me? Listen carefully:` },
    { type: 'pause', ms: 300 },
    ...corePhrase(''),
    { type: 'pause', ms: 2000 },
    { type: 'tts', text: 'Great job! One more time. Listen carefully:' },
    { type: 'pause', ms: 300 },
    ...corePhrase(''),
    { type: 'pause', ms: 2000 },
    { type: 'tts', text: 'Excellent! One more time. Listen carefully:' },
    { type: 'pause', ms: 300 },
    ...corePhrase(''),
  ];
}

/**
 * VP3 Capture — "Your turn" window after repetition practice
 *
 * Short prompt + 3-second silence for the child to speak. The component
 * captures mic audio during the pause for client-side phoneme verification.
 */
export function getPrompt3CaptureAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Your turn! Say the sound for ${name}.` },
    { type: 'pause', ms: 3000 },
  ];
}

/**
 * VP4a — Lowercase Tracing Passive (watch video)
 *
 * "Let's learn how to trace little [letter name].
 *  First, watch me trace little [letter name]."
 *
 * After audio, state machine plays the lowercase tracing video.
 */
export function getPrompt4aAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Let's learn how to trace little ${name}. First, watch me trace little ${name}.` },
  ];
}

/**
 * VP4b — Lowercase Tracing Active (child traces)
 *
 * "Ok, it's your turn! Can you trace little [letter name]?
 *  You can trace carefully on your card with a black marker.
 *  Take your time, and let me know when you're ready to continue."
 */
export function getPrompt4bAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Ok, it's your turn! Can you trace little ${name}? You can trace carefully on your card with a black marker. Take your time, and let me know when you're ready to continue.` },
  ];
}

/**
 * VP5a — Uppercase Tracing Passive (watch video)
 *
 * "Let's learn how to trace big [letter name].
 *  First, watch me trace big [letter name]."
 */
export function getPrompt5aAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Let's learn how to trace big ${name}. First, watch me trace big ${name}.` },
  ];
}

/**
 * VP5b — Uppercase Tracing Active (child traces)
 *
 * "Ok, it's your turn! Can you trace big [letter name]?
 *  You can trace carefully on your card with a black marker.
 *  Take your time, and let me know when you're ready to continue."
 */
export function getPrompt5bAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Ok, it's your turn! Can you trace big ${name}? You can trace carefully on your card with a black marker. Take your time, and let me know when you're ready to continue.` },
  ];
}

/**
 * Scan prompt audio — played when awaiting a card scan.
 *
 * "Let's start! Please find [letter name] and scan the card."
 */
export function getScanPromptAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Let's start! Please find ${name} and scan the card.` },
  ];
}

/**
 * Nudge audio — played after completing an out-of-sequence card.
 * Gently guides the user back to the suggested order.
 */
export function getNudgeAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Great work! Now let's continue with our order. Please find ${name} and scan the card.` },
  ];
}

// =============================================================================
// Legacy API (kept for backward compatibility during transition)
// =============================================================================

/** @deprecated Use getPrompt1Audio */
export function getIntroAudio(phoneme: string): AudioSegment[] {
  return getPrompt1Audio(phoneme);
}

/** @deprecated Use getPrompt4bAudio */
export function getTraceAudio(phoneme: string): AudioSegment[] {
  return getPrompt4bAudio(phoneme);
}

/** @deprecated */
export function getCongratsAudio(phoneme: string): AudioSegment[] {
  const name = getLetterName(phoneme);
  return [
    { type: 'tts', text: `Great job! Let's work hard to remember what` },
    { type: 'pause', ms: 100 },
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 100 },
    { type: 'tts', text: 'looks like.' },
  ];
}

/** @deprecated Use getPrompt2PreVideoAudio + getPrompt2PostVideoAudio */
export function getIllustrationAudio(phoneme: string, illustrationWord: string): AudioSegment[] {
  return [
    { type: 'phoneme', phoneme },
    { type: 'pause', ms: 150 },
    { type: 'tts', text: `looks like...${illustrationWord}!` },
  ];
}

/** @deprecated Use getPrompt2PostVideoAudio */
export function getDrawAudio(illustrationWord: string): AudioSegment[] {
  return [
    { type: 'tts', text: `It's your turn to draw! Can you draw a ${illustrationWord} like mine? You can use colored markers to draw your very own ${illustrationWord}. Take your time, and let me know when you're ready to continue!` },
  ];
}

/** Static fallback phrases per round */
export function getFallbackPhrase(phoneme: string, round: 1 | 2): string {
  if (round === 1) {
    return 'This sound is';
  }
  const word = getIllustrationWord(phoneme);
  return `looks like...${word}!`;
}
